"use client";

import React, { useEffect, useState } from 'react';

const DEBUG_REPORT_BUILD = process.env.NEXT_PUBLIC_DEBUG_AUTH_REPORT === 'true';

function isDebugEnabledRuntime() {
  if (DEBUG_REPORT_BUILD) return true;
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug_auth_report') === '1') return true;
    if (window.localStorage.getItem('DEBUG_AUTH_REPORT') === 'true') return true;
  } catch {
    // ignore
  }
  return false;
}
const SENSITIVE_KEY_REGEX = /password|pass|pwd|token|secret|authorization|auth|cookie|set-cookie/i;

function sanitizeSensitive(value: any): any {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(sanitizeSensitive);
  if (value instanceof Error) return { name: value.name, message: value.message, stack: value.stack };
  if (typeof value === 'object') {
    const out: Record<string, any> = {};
    for (const k of Object.keys(value)) {
      try {
        if (SENSITIVE_KEY_REGEX.test(k)) {
          out[k] = 'REDACTED';
        } else {
          out[k] = sanitizeSensitive((value as any)[k]);
        }
      } catch (_) {
        out[k] = 'UNSERIALIZABLE';
      }
    }
    return out;
  }
  try {
    return String(value);
  } catch {
    return 'UNSERIALIZABLE';
  }
}

function buildErrorReport(error: unknown, extra?: any) {
  const safeError = (() => {
    try {
      if (error instanceof Error) return sanitizeSensitive({ name: error.name, message: error.message, stack: error.stack });
      return sanitizeSensitive(error);
    } catch (_) {
      return { message: String(error) };
    }
  })();

  const report = {
    timestamp: new Date().toISOString(),
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? 'unknown',
    buildId: process.env.NEXT_PUBLIC_BUILD_ID ?? 'unknown',
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    language: typeof navigator !== 'undefined' ? navigator.language : '',
    error: safeError,
    extra: sanitizeSensitive(extra ?? {}),
  };

  return JSON.stringify(report, null, 2);
}

function ErrorReportModal({ open, report, onClose }: { open: boolean; report: string; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Relatório de erro</h3>
          <button onClick={onClose} className="text-sm text-gray-600">Fechar</button>
        </div>
        <p className="text-sm text-gray-600 mb-2">Copie o texto abaixo e envie para o suporte. Dados sensíveis foram REDACTED.</p>
        <textarea readOnly value={report} className="w-full h-56 p-2 text-xs font-mono border rounded resize-y" />
        <div className="mt-3 flex gap-2 justify-end">
          <button
            onClick={() => { if (typeof navigator !== 'undefined' && navigator.clipboard) navigator.clipboard.writeText(report); }}
            className="bg-blue-600 text-white px-3 py-2 rounded"
          >
            Copiar relatório
          </button>
          <button onClick={onClose} className="bg-gray-200 text-gray-800 px-3 py-2 rounded">Fechar</button>
        </div>
      </div>
    </div>
  );
}

export default function ErrorReporterClient() {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState('');

  useEffect(() => {
     if (!isDebugEnabledRuntime()) return;

    const onError = (e: ErrorEvent) => {
      try {
        const payload = { message: e.message, filename: e.filename, lineno: e.lineno, colno: e.colno, stack: (e.error && (e.error as any).stack) || '' };
        const r = buildErrorReport(payload, { eventType: 'error' });
        setReport(r);
        setOpen(true);
      } catch (err) {
        // ignore
      }
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      try {
        const reason = (e as any).reason ?? 'Unhandled rejection';
        const r = buildErrorReport(reason, { eventType: 'unhandledrejection' });
        setReport(r);
        setOpen(true);
      } catch (err) {
        // ignore
      }
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  if (!isDebugEnabledRuntime()) return null;

  return <ErrorReportModal open={open} report={report} onClose={() => setOpen(false)} />;
}
